import { ReadonlyVec2, ReadonlyVec3, vec2 } from 'gl-matrix';
import { DoseMatrix } from '../../../domain/patient/doseMatrix';
import { gl } from '../../Engine';

/**
 * {@link DoseTexture} is an engine presentation of {@link Dose}.\
 * It inherits from {@link SliceSource} as it is made up of {@link SliceData slices}.\
 * It contains the {@link WebGLTexture texture} data and physical properties to map that texture into PatientSpace.
 */
export class DoseTexture {
    /**
     * {@link WebGLTexture} containing the dose distribution texture data.\
     * It has an internalFormat of {@link gl.R16F}, format {@link gl.RED} and type {@link gl.FLOAT}.\
     * This should be {@link delete deleted} when the {@link DoseTexture} is no more needed.
     */
    private readonly texture: WebGLTexture;

    /**
     * This is the range of color values within this {@link DoseTexture}.\
     * The first (x)-coordinate is the smallest color value within the dose distribution and\
     * the second (y)-coordinate is the greatest color value within the dose distribution.
     */
    private _range: vec2;

    /**
     * The physical dimensions of the {@link DoseTexture}.\
     * This is the size of the {@link texture} in pixels.
     */
    private _size: ReadonlyVec3;

    /**
     * The actual dose data in the form of a {@link Float32Array}.\
     * This is the actual pixel data that is stored in the {@link texture}.
     */
    private _dosePixels: Float32Array;

    /**
     * Constructor that will generate {@link DoseTexture} from {@link Dose} datamodel.\
     * This defines the physical dimensions and transformations to convert points between ImageSpace and PatientSpace.\
     * It then generates the {@link SliceData} needed for {@link SliceSource}.\
     * Converts the {@link Dose.pixelData} into suitable format.\
     * Determines the {@link _range valueRange} of the dose data.
     * Generates {@link WebGLTexture} and populates it with the {@link Dose.pixelData}.\
     * @param dose TODO: {@link Dose} assumes constant slice spacing. As a result {@link DoseObject} also relies on that assumption.\
     * {@link DoseObject} would not however require significant changes to support varying slice spacing as well.
     */
    public constructor(dose: DoseMatrix) {
        const sizePx = dose.grid.getSize();

        const texture = gl.createTexture();
        if (!texture) {
            throw new Error(`Unable to create WebGLTexture for ImageObject.`);
        }
        gl.bindTexture(gl.TEXTURE_3D, texture);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_BASE_LEVEL, 0);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAX_LEVEL, 0);

        // gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
        // gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_3D, gl.TEXTURE_WRAP_R, gl.CLAMP_TO_EDGE);

        this._dosePixels = dose.realPixels;
        let min;
        let max = (min = this._dosePixels[0]);
        for (const f of this._dosePixels) {
            if (f < min) {
                min = f;
            } else if (f > max) {
                max = f;
            }
        }

        this._range = vec2.fromValues(min, max);

        this._size = sizePx;
        gl.texImage3D(
            gl.TEXTURE_3D, // target
            0, // level
            gl.R16F, // internalformat
            sizePx[0], // width
            sizePx[1], // height
            sizePx[2], // depth
            0, // border
            gl.RED, // format
            gl.FLOAT, // type
            this._dosePixels // pixel
        );
        this.texture = texture;
    }

    /**
     * Method to get the range of color values within this dose texture.
     */
    public get range(): ReadonlyVec2 {
        return this._range;
    }

    /**
     * Method to {@link gl.bindTexture bind} the {@link texture dose texture}.
     */
    public bindTexture(): void {
        gl.bindTexture(gl.TEXTURE_3D, this.texture);
    }

    /**
     * Method to get the dose value at a given coordinate.\
     * This method will return null if the coordinate is outside the bounds of the dose texture.
     * @param point The coordinate in PatientSpace for which the dose value is required.
     * @returns The dose value at the given coordinate
     * or null if the coordinate is outside the bounds of the dose texture.
     */
    public getDoseIntensityValueAtCursorCoordinate(
        point: ReadonlyVec3
    ): number | null {
        if (!this._dosePixels?.length) {
            return null;
        }
        const x = Math.floor(point[0]);
        const y = Math.floor(point[1]);
        const z = point.length > 2 ? Math.floor(point[2]) : 0;
        const [sizeX, sizeY, sizeZ] = this._size;
        if (x < 0 || y < 0 || z < 0 || x >= sizeX || y >= sizeY || z >= sizeZ) {
            return null;
        }
        const index = x + y * sizeX + z * sizeX * sizeY;
        const doseValue = this._dosePixels[index];
        return isNaN(doseValue) || doseValue === undefined ? null : doseValue;
    }

    /**
     * Method to delete the {@link DoseTexture}.\
     * This will {@link gl.deleteTexture delete} the {@link texture}, deallocating all the WebGL memory consumed by this {@link DoseTexture}.
     * @returns True to indicate that the {@link texture} was successfully deleted.
     */
    public delete(): boolean {
        gl.deleteTexture(this.texture);
        return true;
    }
}
